import React from 'react';
import { Wrench, StickyNote, User, Gauge, ClipboardList } from 'lucide-react';
import {
    groupedObservationsFor,
    OBSERVATION_STATUS_STYLES,
} from '../utils/serviceRecordObservations';

/**
 * Fleet portal — the expanded detail of one service record.
 *
 * Mirrors the workshop's VehicleServiceRecordDetail presentation (same IN/OUT
 * observation grid, same RED/AMBER/GREEN palette) so a fleet customer sees what
 * their workshop sees. The pairing logic itself is shared via
 * utils/serviceRecordObservations.
 *
 * Two deliberate differences from the workshop component:
 *
 *   1. IMAGES. The workshop fetches each note image by asking the server to presign
 *      an S3 key it supplies. That endpoint presigns any key beginning `job/`, so
 *      handing it to an external fleet customer would let them pull any job image in
 *      the workshop. Here the URLs arrive already presigned in the detail response
 *      (`noteImageUrls`, keyed by s3Key), generated server-side from keys already
 *      inside a record the caller was verified to own. The client never asks the
 *      server to sign a key of its choosing.
 *
 *   2. NO INVOICE PDF. The snapshot carries `invoicePdfS3Key`, but invoices live on
 *      the fleet portal's own Invoices page with their own ownership check — and
 *      surfacing it here would mean exposing a second key-accepting endpoint.
 */

// ============================================================================
// Observation cells
// ============================================================================

/** One phase (IN or OUT). Null when that phase wasn't recorded. */
const ObservationPhaseCell = ({ observation }) => {
    if (!observation) {
        return (
            <div className="px-2 py-1 rounded text-xs border border-dashed border-gray-200 bg-gray-50/50 text-gray-300 italic">
                — not recorded
            </div>
        );
    }

    const status = observation.observationStatus || 'UNCLASSIFIED';
    const cls = OBSERVATION_STATUS_STYLES[status] || OBSERVATION_STATUS_STYLES.UNCLASSIFIED;

    return (
        <div className={`flex items-center justify-between gap-2 px-2 py-1 rounded text-xs border ${cls}`}>
            <span className="truncate">{observation.value || '—'}</span>
            <span className="font-mono text-[10px] uppercase tracking-wider shrink-0">
                {status}
            </span>
        </div>
    );
};

/**
 * One paired row: label cell, then the IN and OUT cells side by side.
 *
 * The label cell's contents depend on where the row sits — inside a position
 * group the name and position chip are already shown as headers above, so the
 * row only carries its sub-position chip (if any).
 */
const ObservationPairRow = ({ pair, showName = true, showPositionLabel = true }) => {
    const hasNameLine = showName && !!pair.name;
    const hasPositionChip = showPositionLabel && !!pair.positionLabel;
    const hasSubChip = !!pair.subPositionLabel;

    return (
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-1.5 items-center">
            <div className="px-2 py-1 text-xs">
                {hasNameLine && (
                    <div className="font-semibold text-gray-700 truncate">{pair.name}</div>
                )}
                {(hasPositionChip || hasSubChip) && (
                    <div className={`flex items-center gap-1 flex-wrap ${hasNameLine ? 'mt-0.5' : ''}`}>
                        {hasPositionChip && (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wide">
                                {pair.positionLabel}
                            </span>
                        )}
                        {hasSubChip && (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 text-[10px] font-bold uppercase tracking-wide">
                                {pair.subPositionLabel}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <ObservationPhaseCell observation={pair.in} />
            <ObservationPhaseCell observation={pair.out} />
        </div>
    );
};

/**
 * The full observation grid for one work item — grouped by measurement, then by
 * position, with an IN / OUT column header.
 */
const ObservationGrid = ({ workItem }) => {
    const grouped = groupedObservationsFor(workItem);
    if (grouped.length === 0) return null;

    return (
        <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <ClipboardList size={13} />
                Checks
            </p>

            {/* Column headers */}
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-1.5 mb-1.5">
                <div />
                <div className="px-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    On arrival
                </div>
                <div className="px-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    On completion
                </div>
            </div>

            <div className="space-y-3">
                {grouped.map((measurement) => (
                    <div key={measurement.measurementUuid} className="space-y-1">
                        {/* Measurement name — shown once, above its position groups. */}
                        <p className="text-xs font-semibold text-gray-700 px-2">
                            {measurement.name}
                        </p>

                        {measurement.positions.map((positionGroup) => (
                            <div
                                key={`${measurement.measurementUuid}::${positionGroup.positionLabel ?? '_'}`}
                                className="space-y-1"
                            >
                                {/* Position header only when the measurement is positioned
                                    (e.g. LF / RF). Single-slot measurements skip it. */}
                                {positionGroup.positionLabel && (
                                    <div className="px-2">
                                        <span className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wide">
                                            {positionGroup.positionLabel}
                                        </span>
                                    </div>
                                )}

                                {positionGroup.pairs.map((pair, i) => (
                                    <ObservationPairRow
                                        key={`${pair.measurementUuid}::${pair.positionLabel ?? ''}::${pair.subPositionLabel ?? ''}::${i}`}
                                        pair={pair}
                                        // Name and position are already headers above.
                                        showName={false}
                                        showPositionLabel={false}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

// ============================================================================
// Work items & notes
// ============================================================================

const OUTCOME_STYLES = {
    COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const titleCase = (s) =>
    (s || '').split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

const shortDateTime = (instant) =>
    !instant
        ? ''
        : new Date(instant).toLocaleString('en-AU', {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
        });

const WorkItemCard = ({ workItem }) => (
    <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{workItem.workItemName}</p>
                {workItem.costDetail?.labour?.description && (
                    <p className="text-xs text-gray-500 mt-0.5">
                        {workItem.costDetail.labour.description}
                    </p>
                )}
                {workItem.workItemStaffComment && (
                    <p className="text-xs text-gray-600 mt-1">{workItem.workItemStaffComment}</p>
                )}
            </div>

            {workItem.outcome && (
                <span className={[
                    'inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium border flex-shrink-0',
                    OUTCOME_STYLES[workItem.outcome] || 'bg-gray-100 text-gray-600 border-gray-200',
                ].join(' ')}>
                    {titleCase(workItem.outcome)}
                </span>
            )}
        </div>

        <ObservationGrid workItem={workItem} />
    </div>
);

const NoteCard = ({ note, imageUrls }) => (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
        {note.noteText && (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.noteText}</p>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
            {note.linkedWorkItemName && (
                <span className="text-xs text-gray-400">{note.linkedWorkItemName}</span>
            )}
            {note.createdAt && (
                <span className="text-xs text-gray-400">{shortDateTime(note.createdAt)}</span>
            )}
        </div>

        {note.images?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
                {note.images.map((img) => {
                    // Already presigned server-side, from keys inside this record.
                    const url = imageUrls[img.s3Key];
                    if (!url) return null;
                    return (
                        <a
                            key={img.imageUuid || img.s3Key}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="block w-20 h-20 rounded-lg overflow-hidden border border-gray-200 hover:border-emerald-300 transition-colors"
                        >
                            <img
                                src={url}
                                alt={img.originalFilename || 'Service note'}
                                className="w-full h-full object-cover"
                            />
                        </a>
                    );
                })}
            </div>
        )}
    </div>
);

// ============================================================================
// Job entry
// ============================================================================

const JobEntryCard = ({ jobKey, entry, imageUrls }) => (
    <div className="space-y-4">
        {/* Job meta */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
            {entry.serviceProfileName && (
                <span className="text-sm font-medium text-gray-900">
                    {entry.serviceProfileName}
                </span>
            )}
            {entry.technician && (
                <span className="text-xs text-gray-500 flex items-center gap-1.5">
                    <User size={12} className="text-gray-400" />
                    {entry.technician}
                </span>
            )}
            {entry.odometerOut != null && (
                <span className="text-xs text-gray-500 flex items-center gap-1.5">
                    <Gauge size={12} className="text-gray-400" />
                    {entry.odometerOut.toLocaleString()} km
                </span>
            )}
            <span className="text-xs text-gray-400 font-mono ml-auto">{jobKey}</span>
        </div>

        {/* Work items */}
        {entry.workItems?.length > 0 && (
            <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                    <Wrench size={13} />
                    Work carried out
                </p>
                <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                    {entry.workItems.map((wi, i) => (
                        <WorkItemCard key={i} workItem={wi} />
                    ))}
                </div>
            </div>
        )}

        {/* Notes */}
        {entry.notes?.length > 0 && (
            <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                    <StickyNote size={13} />
                    Notes
                </p>
                <div className="space-y-3">
                    {entry.notes.map((note, i) => (
                        <NoteCard key={i} note={note} imageUrls={imageUrls} />
                    ))}
                </div>
            </div>
        )}
    </div>
);

// ============================================================================
// Detail
// ============================================================================

/**
 * @param {object} record FleetServiceRecordDetail from GET /api/fleet/service-records/{id}
 */
const FleetServiceRecordDetail = ({ record }) => {
    if (!record) return null;

    const entries = Object.entries(record.serviceHistory ?? {});
    // s3Key → presigned URL, generated server-side.
    const imageUrls = record.noteImageUrls ?? {};

    if (entries.length === 0) {
        return (
            <div className="px-6 py-6">
                <p className="text-sm text-gray-500">No detail recorded for this service.</p>
            </div>
        );
    }

    return (
        <div className="px-6 py-5 bg-gray-50 space-y-6">
            {entries.map(([jobKey, entry]) => (
                <JobEntryCard
                    key={jobKey}
                    jobKey={jobKey}
                    entry={entry}
                    imageUrls={imageUrls}
                />
            ))}
        </div>
    );
};

export default FleetServiceRecordDetail;